import React, { useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import { makeMutation } from '../../../api/helpers';
import Button from '../../../components/input/Button';
import FlatButton from '../../../components/input/FlatButton';
import LogoIcon from '../../../components/LogoIcon';
import LogoText from '../../../components/LogoText';
import PricingCard from '../../../components/PricingCard';
import Spacer from '../../../components/Spacer';
import SubscribeAccountSelector, {
  DropOption,
} from '../../../components/SubscribeAccountSelector/SubscribeAccountSelector';
import { Subscribe_service } from './__generated__/Subscribe_service.graphql';
import { Subscribe_viewer } from './__generated__/Subscribe_viewer.graphql';
import { SubscribeToService } from '../../../api/mutations';
import { useToastDispatch } from '../../../components/Toast/ToastProvider';
import { Link, useRouter } from 'found';
import CostCalculator from '../../../components/CostCalculator';
import { ApiHeader } from '../../../components/ApiHeader/ApiHeader';
import Decimal from 'decimal.js';
import {
  LightParagraph,
  Paragraph,
  SmallTitle,
} from '../../../components/typography/Typography';
import { formatCurrency } from '../../../helpers';

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 100px;
  align-items: center;
`;

const CenteredContent = styled.div`
  width: 500px;
`;

const Title = styled.h2`
  font-weight: 500;
  font-size: 23px;
`;

const LogoCont = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 80px;
`;

const FeeExplanation = styled.div`
  display: flex;
  align-items: center;
`;

const FeeText = styled.div``;

const FeeAmount = styled.div`
  width: 100px;
  text-align: right;
`;

const RuleContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Rule = styled.div`
  width: 80%;
  height: 1px;
  background: #ededfb;
`;

const UnsupportedText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

type Props = {
  viewer?: Subscribe_viewer;
  service: Subscribe_service;
  subscriptionFee: number;
};

function Subscribe({ viewer, service, subscriptionFee }: Props) {
  const isAuthed = viewer && viewer.id;

  const accounts =
    viewer?.userAccounts?.map((account) => ({
      id: account.id,
      accountName: account.name,
      paymentMethod: account.subscriber?.defaultMethod?.last4
        ? {
            last4: account.subscriber?.defaultMethod?.last4,
          }
        : undefined,
      currency: account.subscriber?.currency,
    })) ?? [];

  const [selectedAccount, setSelectedAccount] = useState<DropOption>(
    accounts[0] || undefined
  );
  const [accountError, setAccountError] = useState<any | undefined>(undefined);

  const dispatch = useToastDispatch();
  const { router, match } = useRouter();
  if (isAuthed && accounts.length === 0) {
    return <div>Err</div>;
  }

  async function onSubscribe() {
    try {
      if ((service.charges?.edges ?? []).length === 0) {
        dispatch({
          text: `No charges to subscribe to`,
          alert: 'error',
          stayFor: 4000,
        });
        return;
      }
      const currency = service.charges?.edges?.[0]?.node?.price.currency;

      const resp: any = await makeMutation(SubscribeToService, {
        accountID: selectedAccount.id,
        serviceID: service.id,
        currency,
      });
      dispatch({
        text: `Subscribed to ${service.name}`,
        alert: 'success',
        stayFor: 4000,
      });
      router.push(`/subscriptions/${resp.subscribe.id}`);
    } catch (err) {
      switch (err.type) {
        case 'ErrPaymentMethodNotFound':
          setAccountError(err);
          break;
      }

      dispatch({ text: err.toString(), alert: 'error', stayFor: 4000 });
    }
  }

  let accountErrorElement = undefined;

  if (accountError) {
    switch (accountError.type) {
      case 'ErrPaymentMethodNotFound':
        accountErrorElement = (
          <p>
            This account has not setup a payment method. Set one up in your
            <Link to='/account-settings/billing'> account settings</Link>
          </p>
        );
        break;
      default:
        accountErrorElement = <p>{accountError.toString()}</p>;
    }
  }

  let paidAPI = false;
  service.charges?.edges?.forEach((charge) => {
    if (charge?.node?.price.value !== 0) {
      paidAPI = true;
    }
  });

  const cardMissing = paidAPI && !selectedAccount?.paymentMethod;

  const isCurrencyCompatible = selectedAccount
    ? service.supportedCurrencies.includes(selectedAccount?.currency)
    : true;

  return (
    <Container>
      <CenteredContent>
        <ApiHeader
          imageURL={service.imageURL || ''}
          title={service.name || ''}
          description={service.description}
        />
        <Spacer vertical spacing={4} />
        <Title>Pricing</Title>
        <Spacer vertical spacing={3} />

        <CostCalculator service={service} subscriptionFee={subscriptionFee} />
        <Spacer vertical spacing={2} />
        {service.charges?.edges?.map((charge) => (
          <>
            <PricingCard
              chargeType={charge?.node?.chargeType ?? 'Unknown charge type'}
              title={charge?.node?.name ?? ''}
              description={charge?.node?.description ?? ''}
              chargePrice={new Decimal(charge?.node?.price.value ?? 0)
                .add(new Decimal(charge?.node?.salesTax.value ?? 0))
                .toNumber()}
              currency={charge?.node?.price.currency ?? ''}
            />
            <Spacer vertical spacing={1} />
          </>
        ))}

        {paidAPI && (
          <>
            <Spacer vertical spacing={4} />
            <RuleContainer>
              <Rule />
            </RuleContainer>
            <Spacer vertical spacing={4} />
            <FeeExplanation>
              <FeeText>
                <SmallTitle>Processing Fee</SmallTitle>
                <Spacer vertical spacing={1} />
                <LightParagraph>
                  You are charged this fee once in any month that you make at
                  least one paid request to the API.
                </LightParagraph>
              </FeeText>
              <FeeAmount>
                <Paragraph>
                  {formatCurrency(
                    selectedAccount?.currency ?? 'gbp',
                    subscriptionFee
                  )}
                </Paragraph>
              </FeeAmount>
            </FeeExplanation>
          </>
        )}
        <Spacer vertical spacing={4} />
        <RuleContainer>
          <Rule />
        </RuleContainer>
        <Spacer vertical spacing={4} />
        {isAuthed ? (
          <>
            {accountErrorElement}
            <SubscribeAccountSelector
              options={accounts}
              selected={selectedAccount}
              onChange={(val) => {
                setSelectedAccount(val);
              }}
            />
            <Spacer vertical spacing={2} />
            {isCurrencyCompatible ? (
              <Button
                color='white'
                onClick={onSubscribe}
                disabled={cardMissing}
              >
                {cardMissing
                  ? 'Setup a payment method to subscribe'
                  : 'Subscribe to API'}
              </Button>
            ) : (
              <UnsupportedText>
                <SmallTitle>No supported currency</SmallTitle>
                <LightParagraph>
                  Your currency{' '}
                  {selectedAccount?.currency
                    ? `(${selectedAccount?.currency})`
                    : ''}{' '}
                  isn't supported by this API yet.
                </LightParagraph>
              </UnsupportedText>
            )}
          </>
        ) : (
          <>
            <Button
              color='white'
              onClick={() => {
                router.push({
                  pathname: `/preauth/customer-sign-up`,
                  query: { redirect: match.location.pathname },
                });
              }}
            >
              Register to subscribe
            </Button>
            <Spacer vertical spacing={1} />
            <FlatButton
              color='#5C6EF8'
              darkenOnHover
              onClick={() => {
                router.push({
                  pathname: `/preauth/login`,
                  query: { redirect: match.location.pathname },
                });
              }}
            >
              Login
            </FlatButton>
          </>
        )}

        <Spacer vertical spacing={3} />
        <LogoCont
          onClick={() => {
            window.open('https://paypi.dev', '_blank');
          }}
        >
          <LogoIcon height={'14px'} color={'#9092aa'} />
          <LogoText color={'#9092aa'} height={'16px'} />
        </LogoCont>
      </CenteredContent>
    </Container>
  );
}

export default createFragmentContainer(Subscribe, {
  viewer: graphql`
    fragment Subscribe_viewer on User {
      id
      userAccounts {
        id
        name
        subscriber {
          defaultMethod {
            last4
          }
          currency
        }
      }
    }
  `,
  service: graphql`
    fragment Subscribe_service on Service {
      id
      name
      description
      imageURL
      supportedCurrencies
      charges(first: 100) {
        edges {
          node {
            id
            chargeType
            name
            description
            price {
              value
              currency
            }
            metricName
            salesTax {
              value
              currency
            }
          }
        }
      }
      ...CostCalculator_service
    }
  `,
});
